import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { useAppSelector, useAppDispatch } from "store";
import { closeModal } from "store/modals/slice";

import {
  handleClearFields,
  handleUpdateFields,
  getRiskLimitItem,
  updateRiskLimit,
} from "store/risk/limits/slice";

import { Modal } from "components/Modal/Modal";
import { NotificationTypes, showNotification } from "store/notifications/slice";

import Button from "components/buttons/Button";
import InnerInput from "components/inputs/InnerInput/InnerInput";
import SwitchBtn from "components/buttons/SwitchBtn";
import DefaultDropdown from "components/dropdowns/DefaultDropdown/DefaultDropdown";

import ModalSkeleton from "components/global/Skeleton/ModalSkeleton/ModalSkeleton";
import { useFormErrors } from "helpers/useFormErrors";
import { handleApiError } from "helpers/handleApiError";
import { usePermissionHelper } from "helpers/usePermissionHelper";

const RiskLimitEditModal: React.FC = () => {
  const { t } = useTranslation();

  const dispatch = useAppDispatch();

  const navigate = useNavigate();
  const location = useLocation();

  const [searchParams] = useSearchParams();

  const riskLevel = searchParams.get("riskLevel") as string;
  const operationType = searchParams.get("operationType") as string;
  const currency = searchParams.get("currency") as string;

  const payload = useAppSelector((store) => store.riskLimits.payload);

  const isItemFetching = useAppSelector(
    (store) => store.riskLimits.isItemFetching,
  );
  const formErrors = useFormErrors();
  const permissions = usePermissionHelper();
  const canUpdate = Boolean(permissions?.update);

  useEffect(() => {
    if (!riskLevel || !operationType || !currency) {
      return;
    }

    dispatch(
      getRiskLimitItem({
        riskLevel,
        operationType,
        currency,
      }),
    );
  }, [riskLevel, operationType, currency]);

  const handleApply = async () => {
    if (!payload || !canUpdate) return;

    formErrors.resetErrors();

    const data = {
      riskLevel: payload.riskLevel,
      operationType: payload.operationType,
      currency: payload.currency,
      amount: payload.amount,
      isActive: payload.isActive ? 1 : 0,
    };

    try {
      await dispatch(
        updateRiskLimit({
          riskLevel,
          operationType,
          currency,
          data,
        }),
      ).unwrap();

      dispatch(
        showNotification({
          title: t("success"),
          message: t("entityUpdateSuccess"),
          type: NotificationTypes.success,
        }),
      );


      // handleCloseClick();
    } catch (error) {
      handleApiError({
        error,
        t,
        dispatch,
        formErrors,
      });
    }
  };

  const handleCloseClick = () => {
    navigate(location.pathname);

    dispatch(closeModal());

    dispatch(handleClearFields());
  };

  const handleData = (key: string, value: any | boolean) => {
    if (!canUpdate) return;

    dispatch(handleUpdateFields({ [key]: value }));
  };

  return (
    <Modal onClose={handleCloseClick} size="sm">
      {!payload ? (
        <ModalSkeleton />
      ) : (
        <>
          <Modal.Header>
            {" "}
            Edit limit — {payload.riskLevel} / {payload.operationType}
          </Modal.Header>

          <Modal.Content>
            <div className="risk-limit-modal">
              <div className="risk-limit-modal__row">
                <div className="risk-limit-modal__selectors">
                  <InnerInput
                    value={payload.riskLevel}
                    label={t("riskLevel")}
                    type="text"
                    id="riskLevel"
                    onChange={() => {}}
                    disabled
                  />
                  <InnerInput
                    value={payload.operationType}
                    label={t("operationType")}
                    type="text"
                    id="operationType"
                    onChange={() => {}}
                    disabled
                  />
                  <InnerInput
                    value={payload.currency}
                    label={t("currency")}
                    type="text"
                    id="currency"
                    onChange={() => {}}
                    disabled
                  />
                </div>
                <div className="risk-limit-modal__settings">
                  <div className="risk-limit-modal__amount">
                    <InnerInput
                      label="Amount"
                      type="text"
                      id="amount"
                      value={payload.amount}
                      onChange={(e) => {
                        handleData("amount", e);
                        formErrors.clearFieldError("amount");
                      }}
                      hasError={Boolean(formErrors.errors?.["amount"])}
                      disabled={!canUpdate}
                    />
                  </div>

                  <div className="risk-limit-modal__switch">
                    <SwitchBtn
                      activeTxt={t("active")}
                      notActiveTxt={t("inActive")}
                      isActive={payload.isActive}
                      disabled={!canUpdate}
                      handleClick={() =>
                        handleData("isActive", !payload.isActive)
                      }
                    />
                  </div>
                </div>
              </div>
            </div>
          </Modal.Content>

          {canUpdate && (
          <Modal.Footer>
            <Button
              isSecondary
              txt={t("cancel")}
              handleClick={handleCloseClick}
            />

            <Button
              isPrimary
              txt="Save"
              handleClick={handleApply}
              isLoad={isItemFetching}
            />
          </Modal.Footer>
          )}
        </>
      )}
    </Modal>
  );
};

export default RiskLimitEditModal;
